import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, CheckCircle } from 'lucide-react';

interface WaitlistFormProps {
  source?: string;
  variant?: 'light' | 'dark';
}

export function WaitlistForm({ source, variant = 'light' }: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError('');

    try {
      const response = await fetch('/api/waitlist/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong');
        return;
      }

      setSuccess(true);
      setMessage(data.message);
    } catch {
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setPending(false);
    }
  };

  if (success) {
    return (
      <div className={`flex items-center justify-center gap-2 ${variant === 'dark' ? 'text-success-foreground' : 'text-success-on-glass'}`}>
        <CheckCircle className="h-6 w-6" />
        <span className="font-medium text-lg">{message}</span>
      </div>
    );
  }

  const isLight = variant === 'light';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-md w-full mx-auto">
      <Input
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className={`w-full py-8 pl-7 pr-5 !text-base md:!text-base placeholder:!text-base ${
          isLight ? '!border-border !bg-surface-raised/70 backdrop-blur-md !shadow-xl focus:!outline-marketing-ring focus-visible:!outline-marketing-ring' : '!border-border !bg-input-bg !shadow-lg'
        }`}
        disabled={pending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={pending}
        className="w-full px-12 py-8 text-md shadow-none"
      >
        {pending ? (
          <>
            <Loader2 className="animate-spin mr-2 h-4 w-4" />
            Joining...
          </>
        ) : (
          'Join Waitlist'
        )}
      </Button>
      {error && <p className={`text-sm mt-2 text-center w-full ${isLight ? 'text-danger-on-glass' : 'text-danger-text'}`}>{error}</p>}
    </form>
  );
}
